import { exactSampleCount } from '../core/time';
import { decodeCanonicalAudio } from './audio';
import { loadFixtureSource } from './corpus';
import { asStructuredError, LabError } from './l3-errors';
import { WorkerImageDecoder } from './image-decoder';
import { OffscreenCanvasRenderer } from './production-renderer';
import { createL3Report, type L3Report } from './l3-report';

export type L3ProbeResult = { report: L3Report };

const VISUAL_FIXTURES = ['V-GIF-SIMPLE', 'V-GIF-IRREGULAR', 'V-GIF-ALPHA', 'V-GIF-LONG', 'V-GIF-INFINITE', 'V-WEBP-ANIM', 'V-NONSQUARE'] as const;
const INVALID_VISUAL_FIXTURES = ['V-STATIC', 'V-CORRUPT', 'V-LIMIT'] as const;

function probeAbort(): AbortSignal {
  return new AbortController().signal;
}

export async function runL3Probe(): Promise<L3ProbeResult> {
  const report = createL3Report();
  const decoder = new WorkerImageDecoder();
  const renderer = new OffscreenCanvasRenderer();
  try {
    for (const id of VISUAL_FIXTURES) {
      let handle: Awaited<ReturnType<WorkerImageDecoder['open']>> | null = null;
      try {
        const source = await loadFixtureSource(id, probeAbort());
        if (id === 'V-GIF-SIMPLE') await decoder.inspect(source, probeAbort());
        handle = await decoder.open(source, probeAbort());
        const frame = await handle.frameAtTicks(0, probeAbort());
        report.measurements.decodedFrames += 1;
        if (id === 'V-GIF-LONG') {
          frame.bitmap.close();
          for (const ticks of handle.info.frameStartTicks.slice(1)) {
            const cachedFrame = await handle.frameAtTicks(ticks, probeAbort());
            cachedFrame.bitmap.close();
            report.measurements.decodedFrames += 1;
          }
          report.fixtures.push({ id, kind: 'visual', status: 'CONFORMANT', detail: `${handle.info.frameCount} frames, ${handle.info.width}x${handle.info.height}; LRU cache exercised` });
          continue;
        }
        if (id === 'V-GIF-SIMPLE') {
          const rendered = await renderer.render({
            layers: [{ bitmap: frame.bitmap, sourceWidth: handle.info.width, sourceHeight: handle.info.height, crop: { mode: 'FIT' }, opacity: 1 }],
            text: { lines: ['BITPOP', 'L3'], fontSizePx: 36, anchorX: 240, anchorY: 400, align: 'center', color: [255, 255, 255, 255], opacity: 1 },
          }, probeAbort());
          rendered.bitmap.close();
          report.measurements.renderedFrames += 1;
        } else if (id === 'V-GIF-ALPHA') {
          const left = await createImageBitmap(frame.bitmap);
          const right = await createImageBitmap(frame.bitmap);
          frame.bitmap.close();
          const rendered = await renderer.render({
            layers: [
              { bitmap: left, sourceWidth: handle.info.width, sourceHeight: handle.info.height, crop: { mode: 'FIT' }, opacity: 0.5 },
              { bitmap: right, sourceWidth: handle.info.width, sourceHeight: handle.info.height, crop: { mode: 'FIT' }, opacity: 0.5 },
            ],
          }, probeAbort());
          rendered.bitmap.close();
          report.measurements.renderedFrames += 1;
        } else {
          frame.bitmap.close();
        }
        report.fixtures.push({ id, kind: 'visual', status: 'CONFORMANT', detail: `${handle.info.frameCount} frames, ${handle.info.width}x${handle.info.height}` });
      } catch (cause) {
        report.fixtures.push({ id, kind: 'visual', status: 'NON_CONFORMANT', detail: cause instanceof Error ? cause.message : String(cause) });
        report.errors.push(asStructuredError(cause, 'VISUAL_PROBE_FAILED', 'l3-probe'));
      } finally {
        await handle?.close().catch((cause: unknown) => report.errors.push(asStructuredError(cause, 'IMAGE_CLOSE_FAILED', 'l3-probe')));
      }
    }
    for (const id of INVALID_VISUAL_FIXTURES) {
      try {
        const handle = await decoder.open(await loadFixtureSource(id, probeAbort()), probeAbort());
        await handle.close();
        throw new LabError('INVALID_MEDIA_ACCEPTED', 'l3-probe', `Invalid fixture ${id} was accepted.`, false);
      } catch (cause) {
        if (cause instanceof LabError && cause.structured.code !== 'INVALID_MEDIA_ACCEPTED') {
          report.fixtures.push({ id, kind: 'invalid', status: 'CONFORMANT', detail: cause.structured.code });
        } else {
          report.fixtures.push({ id, kind: 'invalid', status: 'NON_CONFORMANT', detail: cause instanceof Error ? cause.message : String(cause) });
          report.errors.push(asStructuredError(cause, 'INVALID_MEDIA_PROBE_FAILED', 'l3-probe'));
        }
      }
    }
    for (const id of ['A-SYNTH-OFFSET', 'A-CONSTANT-FADE', 'A-WAV-MONO', 'A-WAV-STEREO', 'A-MP3-44100', 'A-M4A-PRIMING'] as const) {
      try {
        const isOffsetFixture = id === 'A-SYNTH-OFFSET' || id === 'A-M4A-PRIMING';
        const isFadeFixture = id === 'A-CONSTANT-FADE';
        const pcm = await decodeCanonicalAudio({
          source: await loadFixtureSource(id, probeAbort()),
          sourceStartTicks: isOffsetFixture ? 12_345n : 0n,
          frameCount: 24,
          volumePercent: 100,
          fadeInFrames: isFadeFixture ? 1 : 0,
          fadeOutFrames: isFadeFixture ? 1 : 0,
        }, probeAbort());
        if (pcm.sampleCount !== exactSampleCount(24)) throw new LabError('PCM_LENGTH_INVALID', 'l3-probe', `Expected ${exactSampleCount(24)} samples.`);
        if (isOffsetFixture && Math.max(...pcm.channels[0].slice(0, 128).map((sample) => Math.abs(sample))) < 0.2) {
          throw new LabError('AUDIO_OFFSET_INVALID', 'l3-probe', `Offset impulse was not found at the selected start for ${id}.`);
        }
        if (isFadeFixture && (pcm.channels[0][0] !== 0 || pcm.channels[0][pcm.sampleCount - 1] !== 0)) throw new LabError('AUDIO_FADE_INVALID', 'l3-probe', 'Audio fade boundaries are not exact.');
        report.measurements.pcmSamplesPerChannel += pcm.sampleCount;
        report.fixtures.push({ id, kind: 'audio', status: 'CONFORMANT', detail: `${pcm.sampleCount} samples/channel` });
      } catch (cause) {
        report.fixtures.push({ id, kind: 'audio', status: 'NON_CONFORMANT', detail: cause instanceof Error ? cause.message : String(cause) });
        report.errors.push(asStructuredError(cause, 'AUDIO_PROBE_FAILED', 'l3-probe'));
      }
    }
    for (const id of ['A-MULTICHANNEL', 'A-TOO-SHORT'] as const) {
      try {
        await decodeCanonicalAudio({
          source: await loadFixtureSource(id, probeAbort()),
          sourceStartTicks: 0n,
          frameCount: 24,
          volumePercent: 100,
          fadeInFrames: 0,
          fadeOutFrames: 0,
        }, probeAbort());
        throw new LabError('INVALID_MEDIA_ACCEPTED', 'l3-probe', `Invalid fixture ${id} was accepted.`, false);
      } catch (cause) {
        if (cause instanceof LabError && cause.structured.code !== 'INVALID_MEDIA_ACCEPTED') {
          report.fixtures.push({ id, kind: 'invalid', status: 'CONFORMANT', detail: cause.structured.code });
        } else {
          report.fixtures.push({ id, kind: 'invalid', status: 'NON_CONFORMANT', detail: cause instanceof Error ? cause.message : String(cause) });
          report.errors.push(asStructuredError(cause, 'INVALID_AUDIO_PROBE_FAILED', 'l3-probe'));
        }
      }
    }
  } finally {
    renderer.dispose();
    decoder.dispose();
    report.cleanup.rendererDisposed = true;
  }
  report.status = report.errors.length === 0 ? 'CONFORMANT' : 'NON_CONFORMANT';
  return { report };
}
